import { Avatar, AvatarId, isActiveUser, User, userApi, UserId, UserProfile } from "@/api/users";
import { defineQuery, defineQueryOptions, useQuery, useQueryCache } from "@pinia/colada";
import { Pinia } from "pinia";
import { computed } from "vue";

export const USER_KEYS = {
    root: ["users"] as const,
    list: () => [...USER_KEYS.root, "list"] as const,
    avatars: () => [...USER_KEYS.root, "avatars"] as const,
    profile: (id: UserId) => [...USER_KEYS.root, "profile", id] as const,
};

const LOOKUP_STALE_MS = 5 * 60_000;

// readCached* facade reads outlive the useQuery lookup consumers, so these
// entries must never be garbage collected; the opt-out lives in the shared
// options because every ensure of the key replaces the entry's options.
const usersListOptions = {
    key: USER_KEYS.list(),
    query: () => userApi.getUsers(),
    staleTime: LOOKUP_STALE_MS,
    gcTime: false as const,
};

const avatarsOptions = {
    key: USER_KEYS.avatars(),
    query: () => userApi.getAvatars(),
    staleTime: LOOKUP_STALE_MS,
    gcTime: false as const,
};

function isId(v: UserId | string): v is UserId {
    return /^[0-9A-F]{8}-([0-9A-F]{4}-){3}[0-9A-F]{12}?$/i.test(v);
}

export function resolveUser(users: User[] | undefined, id: UserId | string): User | undefined {
    return users?.find((u) => (isId(id) ? u.id === id : u.userName === id));
}

// Reactive cache reads for code that can't consume the composable (store getters).
export function readCachedUsers(): User[] | undefined {
    return useQueryCache().getQueryData<User[]>(USER_KEYS.list());
}

export function readCachedUser(id: UserId | string): User | undefined {
    return resolveUser(readCachedUsers(), id);
}

// Imperative prefetch for code outside component setup (main.ts bootstrap,
// store actions). Reuses the cache: fresh data is not refetched.
export async function ensureUsers(pinia?: Pinia): Promise<User[] | undefined> {
    const queryCache = useQueryCache(pinia);
    const state = await queryCache.refresh(queryCache.ensure<User[]>(usersListOptions));
    return state.data;
}

export async function ensureAvatars(pinia?: Pinia): Promise<Avatar[] | undefined> {
    const queryCache = useQueryCache(pinia);
    const state = await queryCache.refresh(queryCache.ensure<Avatar[]>(avatarsOptions));
    return state.data;
}

export const useUserLookups = defineQuery(() => {
    const usersQuery = useQuery(usersListOptions);
    const avatarsQuery = useQuery(avatarsOptions);

    const users = computed(() =>
        usersQuery.data.value ? new Map(usersQuery.data.value.map((u) => [u.id, u])) : undefined
    );
    const usersByName = computed(() =>
        usersQuery.data.value
            ? new Map(usersQuery.data.value.map((u) => [u.userName, u]))
            : undefined
    );
    const avatars = computed(() =>
        avatarsQuery.data.value ? new Map(avatarsQuery.data.value.map((a) => [a.id, a])) : undefined
    );

    return {
        users,
        usersByName,
        avatars,
        get: (id: UserId | string) =>
            isId(id) ? users.value?.get(id) : usersByName.value?.get(id),
        /**
         * Users who can still be picked. Use this for any picker; use `users`/`get` for rendering
         * historical data, which must keep resolving disabled users.
         */
        activeUsers: computed((): User[] =>
            Array.from(users.value?.values() ?? []).filter(isActiveUser)
        ),
        getAvatar: (id: AvatarId) => avatars.value?.get(id),
    };
});

export const userProfileQuery = defineQueryOptions((id: UserId) => ({
    key: USER_KEYS.profile(id),
    query: (): Promise<UserProfile> => userApi.getProfile(id),
    staleTime: 60_000,
}));
